import { DateTime } from 'luxon'

import { TipoConsumo } from '../../../../core/types/services'
import { Filter } from '@presentation/types/components/organisms'
import { SelectOption } from '@presentation/types/components/molecules'

import { parseToRequest } from '../../../utils/string'

const INPUT_FORMAT = 'dd/MM/yyyy HH:mm'
const REQUEST_FORMAT = "yyyy-MM-dd'T'HH:mm"

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 20
const DEFAULT_SORT_BY = 'data_horario_consumo'
const DEFAULT_SORT_DIR = 'desc'

export const formatCpf = (value?: string) => {
  const digits = value?.replace(/\D/g, '') ?? ''

  if (digits.length !== 11) {
    return value ?? ''
  }

  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export const formatDateTime = (value: string) => {
  if (!value) return ''

  return DateTime.fromISO(value).toFormat('dd/LL/yyyy HH:mm')
}

export const toRequestDateTime = (value?: string) => {
  if (!value) return undefined

  const parsedValue = DateTime.fromFormat(value, INPUT_FORMAT)

  if (!parsedValue.isValid) return undefined

  return parsedValue.toFormat(REQUEST_FORMAT)
}

export const getDefaultDateRange = () => {
  const today = DateTime.now()

  return {
    dataInicio: today.minus({ days: 30 }).startOf('day').toFormat(INPUT_FORMAT),
    dataFim: today
      .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
      .toFormat(INPUT_FORMAT)
  }
}

const cleanParams = (params: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      return (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      )
    })
  )
}

export const getRelatorioConsumoAlmocoTipoConsumoOptions =
  (): SelectOption[] => {
    return [
      { label: 'Todos', value: '' },
      {
        label: '100% Subsidiado',
        value: TipoConsumo.CemPorCentoSubsidiado
      },
      {
        label: 'Parcialmente Subsidiado',
        value: TipoConsumo.ParcialmenteSubsidiado
      }
    ]
  }

export const buildRelatorioConsumoAlmocoRequestParams = (
  params: Record<string, any>,
  options?: {
    includePagination?: boolean
  }
) => {
  const { page, perPage, sortBy, sortDir, dataInicio, dataFim, ...rest } =
    params ?? {}
  const includePagination = options?.includePagination ?? true

  const mergedParams = cleanParams({
    ...(includePagination
      ? {
          page: page ?? DEFAULT_PAGE,
          per_page: perPage ?? DEFAULT_PER_PAGE
        }
      : {}),
    sort_by: sortBy ?? DEFAULT_SORT_BY,
    sort_dir: sortDir ?? DEFAULT_SORT_DIR,
    ...(dataInicio ? { inicio: toRequestDateTime(dataInicio) } : {}),
    ...(dataFim ? { fim: toRequestDateTime(dataFim) } : {}),
    ...rest
  })

  return parseToRequest(mergedParams)
}

export const getRelatorioConsumoAlmocoDefaultQueries = () => {
  const defaultDateRange = getDefaultDateRange()

  return {
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    sortBy: DEFAULT_SORT_BY,
    sortDir: DEFAULT_SORT_DIR,
    ...defaultDateRange
  }
}

export const getRelatorioConsumoAlmocoDefaultValues = () => {
  return {
    tipo_consumo: ''
  }
}

export const getRelatorioConsumoAlmocoFilters = (): Filter[] => {
  return [
    {
      type: {
        value: 'nome',
        label: 'Nome'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'cpf',
        label: 'CPF'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'matricula',
        label: 'Matrícula'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'curso',
        label: 'Curso'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'turno_curso',
        label: 'Turno do curso'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'tipo_consumo',
        label: 'Tipo de consumo'
      },
      description: {
        type: 'select',
        configs: {
          options: getRelatorioConsumoAlmocoTipoConsumoOptions()
        }
      }
    },
    {
      type: {
        value: 'dataInicio',
        label: 'Data/Hora inicial'
      },
      description: {
        type: 'input',
        configs: {
          type: 'datetime-local'
        }
      }
    },
    {
      type: {
        value: 'dataFim',
        label: 'Data/Hora final'
      },
      description: {
        type: 'input',
        configs: {
          type: 'datetime-local'
        }
      }
    }
  ]
}
